import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Navigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Rocket, Users, Building2, TrendingUp, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SECTORS, COUNTRIES, STAGES, SERVICE_TYPES } from "@/lib/constants";
import { toast } from "sonner";

const ROLE_CARDS = [
  { value: "founder", icon: Rocket, descKey: "onboarding.roleFounderDesc" },
  { value: "mentor", icon: Users, descKey: "onboarding.roleMentorDesc" },
  { value: "enabler", icon: Building2, descKey: "onboarding.roleEnablerDesc" },
  { value: "investor", icon: TrendingUp, descKey: "onboarding.roleInvestorDesc" },
];

function MultiSelectBadges({ options, selected, onToggle, testId }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((opt) => {
        const isActive = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              isActive
                ? "bg-[color:var(--brand-primary)] text-white border-[color:var(--brand-primary)]"
                : "bg-transparent border-border hover:bg-accent"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export default function Onboarding() {
  const { t } = useTranslation();
  const { completeOnboarding, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    role: "",
    headline: "",
    bio: "",
    region: "",
    country: "",
    sectors: [],
    company_name: "",
    stage: "",
    pitch_doc_url: "",
    website: "",
    expertise: [],
    years_experience: "",
    linkedin_url: "",
    service_type: "",
    services: [],
    investor_focus: [],
    ticket_size: "",
  });

  if (user?.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const toggleMulti = (field, val) => {
    setForm((f) => {
      const list = f[field].includes(val) ? f[field].filter((x) => x !== val) : [...f[field], val];
      return { ...f, [field]: list };
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, years_experience: form.years_experience ? parseInt(form.years_experience, 10) : 0 };
      await completeOnboarding(payload);
      toast.success(t("onboarding.success"));
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-14" data-testid="onboarding-page">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-semibold tracking-tight">{t("onboarding.title")}</h1>
        <p className="text-muted-foreground mt-2">{t("onboarding.subtitle")}</p>
      </div>
      <Progress value={step === 1 ? 50 : 100} className="mb-10 h-1.5" />

      {step === 1 && (
        <div>
          <h2 className="text-lg font-semibold mb-4">{t("onboarding.step1Title")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {ROLE_CARDS.map((r) => {
              const Icon = r.icon;
              const active = form.role === r.value;
              return (
                <Card
                  key={r.value}
                  onClick={() => update("role", r.value)}
                  className={`cursor-pointer rounded-2xl border p-5 transition-all card-elevate ${
                    active ? "border-[color:var(--brand-primary)] ring-2 ring-[color:var(--focus-ring)]" : "border-border"
                  }`}
                  data-testid="onboarding-role-card"
                >
                  <div className="flex items-start justify-between">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </span>
                    {active && <Check className="h-5 w-5 text-primary" />}
                  </div>
                  <h3 className="font-semibold mt-3">{t(`roles.${r.value}`)}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{t(r.descKey)}</p>
                </Card>
              );
            })}
          </div>
          <Button
            className="w-full mt-8 rounded-xl h-12 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white"
            disabled={!form.role}
            onClick={() => setStep(2)}
            data-testid="onboarding-next-button"
          >
            {t("common.next")}
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <h2 className="text-lg font-semibold mb-2">{t("onboarding.step2Title")}</h2>

          <div>
            <Label>{t("onboarding.headline")}</Label>
            <Input
              className="mt-1.5 rounded-xl"
              value={form.headline}
              onChange={(e) => update("headline", e.target.value)}
              placeholder={t("onboarding.headlinePlaceholder")}
              data-testid="onboarding-headline-input"
            />
          </div>

          <div>
            <Label>{t("onboarding.bio")}</Label>
            <Textarea
              className="mt-1.5 rounded-xl"
              value={form.bio}
              onChange={(e) => update("bio", e.target.value)}
              placeholder={t("onboarding.bioPlaceholder")}
              data-testid="onboarding-bio-textarea"
            />
          </div>

          <div>
            <Label>{t("profileFields.country", { defaultValue: "Country" })}</Label>
            <Select value={form.country} onValueChange={(v) => update("country", v)}>
              <SelectTrigger className="mt-1.5 rounded-xl" data-testid="onboarding-country-select">
                <SelectValue placeholder={t("profileFields.countryPlaceholder", { defaultValue: "Select your country" })} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {COUNTRIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {form.role === "founder" && (
            <>
              <div>
                <Label>{t("onboarding.companyName")}</Label>
                <Input className="mt-1.5 rounded-xl" value={form.company_name} onChange={(e) => update("company_name", e.target.value)} data-testid="onboarding-company-input" />
              </div>
              <div>
                <Label>{t("onboarding.sectors")}</Label>
                <div className="mt-1.5">
                  <MultiSelectBadges options={SECTORS} selected={form.sectors} onToggle={(v) => toggleMulti("sectors", v)} testId="onboarding-sectors" />
                </div>
              </div>
              <div>
                <Label>{t("onboarding.stage")}</Label>
                <Select value={form.stage} onValueChange={(v) => update("stage", v)}>
                  <SelectTrigger className="mt-1.5 rounded-xl" data-testid="onboarding-stage-select">
                    <SelectValue placeholder={t("onboarding.stage")} />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("onboarding.pitchDocUrl")}</Label>
                <Input className="mt-1.5 rounded-xl" value={form.pitch_doc_url} onChange={(e) => update("pitch_doc_url", e.target.value)} placeholder="https://" data-testid="onboarding-pitchdoc-input" />
              </div>
              <div>
                <Label>{t("onboarding.website")}</Label>
                <Input className="mt-1.5 rounded-xl" value={form.website} onChange={(e) => update("website", e.target.value)} placeholder="https://" data-testid="onboarding-website-input" />
              </div>
            </>
          )}

          {form.role === "mentor" && (
            <>
              <div>
                <Label>{t("onboarding.expertise")}</Label>
                <div className="mt-1.5">
                  <MultiSelectBadges options={SECTORS} selected={form.expertise} onToggle={(v) => { toggleMulti("expertise", v); toggleMulti("sectors", v); }} testId="onboarding-expertise" />
                </div>
              </div>
              <div>
                <Label>{t("onboarding.yearsExperience")}</Label>
                <Input type="number" className="mt-1.5 rounded-xl" value={form.years_experience} onChange={(e) => update("years_experience", e.target.value)} data-testid="onboarding-years-input" />
              </div>
              <div>
                <Label>{t("onboarding.linkedinUrl")}</Label>
                <Input className="mt-1.5 rounded-xl" value={form.linkedin_url} onChange={(e) => update("linkedin_url", e.target.value)} placeholder="https://linkedin.com/in/..." data-testid="onboarding-linkedin-input" />
              </div>
            </>
          )}

          {form.role === "enabler" && (
            <>
              <div>
                <Label>{t("onboarding.companyName")}</Label>
                <Input className="mt-1.5 rounded-xl" value={form.company_name} onChange={(e) => update("company_name", e.target.value)} data-testid="onboarding-company-input" />
              </div>
              <div>
                <Label>{t("onboarding.serviceType")}</Label>
                <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
                  <SelectTrigger className="mt-1.5 rounded-xl" data-testid="onboarding-servicetype-select">
                    <SelectValue placeholder={t("onboarding.serviceType")} />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("onboarding.servicesOffered")}</Label>
                <div className="mt-1.5">
                  <MultiSelectBadges options={SERVICE_TYPES} selected={form.services} onToggle={(v) => toggleMulti("services", v)} testId="onboarding-services" />
                </div>
              </div>
              <div>
                <Label>{t("onboarding.website")}</Label>
                <Input className="mt-1.5 rounded-xl" value={form.website} onChange={(e) => update("website", e.target.value)} placeholder="https://" data-testid="onboarding-website-input" />
              </div>
            </>
          )}

          {form.role === "investor" && (
            <>
              <div>
                <Label>{t("onboarding.investorFocus")}</Label>
                <div className="mt-1.5">
                  <MultiSelectBadges options={SECTORS} selected={form.investor_focus} onToggle={(v) => { toggleMulti("investor_focus", v); toggleMulti("sectors", v); }} testId="onboarding-investorfocus" />
                </div>
              </div>
              <div>
                <Label>{t("onboarding.ticketSize")}</Label>
                <Input className="mt-1.5 rounded-xl" value={form.ticket_size} onChange={(e) => update("ticket_size", e.target.value)} placeholder={t("onboarding.ticketSizePlaceholder")} data-testid="onboarding-ticketsize-input" />
              </div>
            </>
          )}

          <div className="flex gap-3 pt-4">
            <Button variant="outline" className="rounded-xl flex-1" onClick={() => setStep(1)} data-testid="onboarding-back-button">
              {t("common.back")}
            </Button>
            <Button
              className="rounded-xl flex-1 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="onboarding-submit-button"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("onboarding.finish")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
